// ============================================
// Đọc lỗi API — MỘT chỗ duy nhất cho toàn store-admin.
//
// BE bổ sung `error_details` (mảng `{field, code, message}`) và `error_code`
// (mã máy cấp ngoài) — lần đầu tiên FE có MÃ để dịch, thay vì phải đoán field
// bằng cách so chuỗi tiếng Anh của DRF (`message.includes("'store'")`), một
// cách làm vỡ ngay khi BE đổi câu chữ hoặc tôn trọng Accept-Language.
//
// BA ràng buộc định hình file này:
//
// 1. BẢN BE MỚI CHƯA LÊN QA. Mọi hàm phải chạy đúng khi `error_details` và
//    `error_code` KHÔNG tồn tại, rồi tự tốt lên khi chúng xuất hiện. Không
//    nhánh nào được trả chuỗi rỗng.
// 2. `errors` (cũ) đôi khi mang DỮ LIỆU chứ không phải lỗi field — ví dụ
//    `{reason: "duplicate_feedback"}` hay `{unmatched: [...]}`. BE cố ý không
//    sinh `error_details` cho những response đó. Vì vậy: đọc `error_details`
//    khi có, fallback về `message` khi không.
// 3. KHÔNG NUỐT THÔNG TIN. Không map được `code` -> dùng câu của server; không
//    có câu nào -> dùng fallback của phía gọi. Người dùng luôn nhận được một
//    câu có nghĩa.
//
// `t` truyền vào qua tham số (không gọi hook) để dùng được cả ngoài component —
// hook TanStack Query, service, handler ngoài React.
// ============================================

/** Chữ ký của `t` từ `useI18n()`. */
export type TranslateFn = (key: string, variables?: Record<string, string | number>) => string;

/** Một lỗi đã chuẩn hoá, bất kể nó tới từ nguồn nào trong envelope. */
export interface NormalizedApiErrorDetail {
  /**
   * Đường dẫn field dạng `items[0].quantity`, hoặc `null` khi là lỗi cấp
   * object (`non_field_errors` của DRF — BE trả `null`, KHÔNG trả chuỗi).
   */
  field: string | null;
  /** Mã máy của DRF. `null` khi lấy từ `errors` cũ (dạng đó không có mã). */
  code: string | null;
  /** Câu gốc của server, có thể rỗng nếu BE chỉ gửi mã. */
  message: string;
}

/** Một đoạn của đường dẫn field: `items[0]` -> `{ key: 'items', indexes: [0] }`. */
export interface FieldPathSegment {
  key: string;
  indexes: number[];
}

export interface ParsedFieldPath {
  /** Nguyên văn như BE gửi, dùng làm khoá `Record` cho form. */
  raw: string;
  segments: FieldPathSegment[];
  /** Đường dẫn đã bỏ chỉ số: `items[0].quantity` -> `items[].quantity`. */
  template: string;
  /** Chỉ số mảng ĐẦU TIÊN (0-based) nếu có — để hiện "Dòng n". */
  rowIndex: number | null;
  /** Đoạn cuối: `items[0].quantity` -> `quantity`. */
  leaf: string;
}

// ============================================
// Bảng map mã -> key i18n
// ============================================

/**
 * `error_details[].code` — mã cấp FIELD của DRF.
 *
 * Câu của DRF cho `max_length`/`min_value` có kèm con số ("no more than 50
 * characters"); bản dịch ở đây là câu chung, cố ý đánh đổi con số lấy việc
 * người dùng đọc được bằng ngôn ngữ của họ. Cần con số thì đọc `message` gốc
 * qua `getApiErrorDetails`.
 */
const FIELD_ERROR_CODE_KEYS: Record<string, string> = {
  required: 'apiErrorRequired',
  blank: 'apiErrorBlank',
  null: 'apiErrorRequired',
  invalid: 'apiErrorInvalid',
  invalid_choice: 'apiErrorInvalidChoice',
  incorrect_type: 'apiErrorInvalid',
  max_length: 'apiErrorMaxLength',
  min_length: 'apiErrorMinLength',
  max_value: 'apiErrorMaxValue',
  min_value: 'apiErrorMinValue',
  unique: 'apiErrorUnique',
  does_not_exist: 'apiErrorDoesNotExist',
  // BE validate IANA timezone (`timezone`, `alcohol_curfew.timezone`) và trả mã
  // này kèm 400. Câu gốc của BE là tiếng Anh nên có mã mới dịch được.
  invalid_timezone: 'apiErrorInvalidTimezone',
};

/**
 * `error_code` cấp ngoài — lỗi KHÔNG phải validation.
 *
 * Nhóm nghiệp vụ (`insufficient_stock`, `store_closed`, ...) lấy từ
 * `default_code` của exception BE, nên ổn định qua các bản deploy.
 */
const TOP_LEVEL_ERROR_CODE_KEYS: Record<string, string> = {
  // Nghiệp vụ
  invalid_state_transition: 'apiErrorInvalidStateTransition',
  insufficient_stock: 'apiErrorInsufficientStock',
  store_closed: 'apiErrorStoreClosed',
  alcohol_curfew_active: 'apiErrorAlcoholCurfewActive',
  age_verification_required: 'apiErrorAgeVerificationRequired',
  restricted_sale_blocked: 'apiErrorRestrictedSaleBlocked',
  idempotency_conflict: 'apiErrorIdempotencyConflict',
  payment_error: 'apiErrorPaymentError',
  // Hạ tầng / DRF
  token_expired: 'apiErrorTokenExpired',
  access_denied: 'apiErrorAccessDenied',
  permission_denied: 'apiErrorAccessDenied',
  not_authenticated: 'apiErrorNotAuthenticated',
  authentication_failed: 'apiErrorAuthenticationFailed',
  rate_limit_exceeded: 'apiErrorRateLimitExceeded',
  throttled: 'apiErrorRateLimitExceeded',
  not_found: 'apiErrorNotFound',
  method_not_allowed: 'apiErrorMethodNotAllowed',
  parse_error: 'apiErrorInvalid',
};

/**
 * Mã tổng hợp ở FE (không tới từ BE) cho lỗi tầng vận chuyển. `parseAxiosError`
 * gắn câu tiếng Anh cứng cho các trường hợp này, dịch được thì dịch.
 */
const CLIENT_ERROR_CODE_KEYS: Record<string, string> = {
  network_error: 'apiErrorNetwork',
  timeout: 'apiErrorTimeout',
};

/** Toàn bộ bảng map, gộp lại — `error_details[].code` và `error_code` không đụng nhau. */
export const API_ERROR_CODE_I18N_KEYS: Record<string, string> = {
  ...FIELD_ERROR_CODE_KEYS,
  ...TOP_LEVEL_ERROR_CODE_KEYS,
  ...CLIENT_ERROR_CODE_KEYS,
};

/**
 * `message` của DRF khi lỗi validate — không nói được gì, chỉ dùng khi thật sự
 * không còn nguồn nào khác.
 */
const DRF_VALIDATION_MESSAGE = 'validation error';

// ============================================
// Dịch
// ============================================

/**
 * Dịch, và phân biệt được "chưa có bản dịch" với "đã dịch".
 *
 * `useI18n().t` trả về CHÍNH KEY khi thiếu bản dịch. Không phát hiện ra thì
 * giao diện hiện chuỗi `apiErrorRequired` — tệ hơn cả câu tiếng Anh của server.
 * Trả `null` để phía gọi rơi về `message`, nhờ vậy file này chạy được ngay cả
 * trước khi các key mới được merge vào locale.
 */
function translateOrNull(
  t: TranslateFn,
  key: string,
  variables?: Record<string, string | number>
): string | null {
  const value = t(key, variables);
  if (!value || value === key) return null;
  return value.trim() || null;
}

/**
 * Dịch, có câu dự phòng khi key CHƯA được merge vào locale.
 *
 * Dùng cho nhãn field và câu fallback: mọi key mới đều đi qua đây để giai đoạn
 * "code đã lên, locale chưa lên" không hiện tên key ra màn hình.
 */
export function translateOrFallback(
  t: TranslateFn,
  key: string,
  fallbackText: string,
  variables?: Record<string, string | number>
): string {
  return translateOrNull(t, key, variables) ?? fallbackText;
}

/**
 * Map một mã lỗi (cấp field hoặc cấp ngoài) sang câu đã dịch.
 * Trả `null` khi không map được — CỐ Ý, để phía gọi dùng câu của server.
 */
export function translateApiErrorCode(code: string | null | undefined, t: TranslateFn): string | null {
  if (!code) return null;
  const key = API_ERROR_CODE_I18N_KEYS[code.trim().toLowerCase()];
  if (!key) return null;
  return translateOrNull(t, key);
}

// ============================================
// Parse đường dẫn field
// ============================================

const SEGMENT_PATTERN = /^([^[\]]*)((?:\[\d+\])*)$/;
const INDEX_PATTERN = /\[(\d+)\]/g;

/**
 * Tách `items[0].quantity` thành các đoạn.
 *
 * CHỈ hỗ trợ dạng `[n]` — BE đã chốt không bao giờ trả `items.0.quantity`, nên
 * ở đây không có nhánh đoán số trong đoạn đường dẫn. Chuỗi lạ không khớp mẫu
 * vẫn được giữ nguyên làm một đoạn, để không đánh mất khoá field của form.
 */
export function parseFieldPath(field: string): ParsedFieldPath {
  const raw = field.trim();
  const segments: FieldPathSegment[] = raw.split('.').map((part) => {
    const match = SEGMENT_PATTERN.exec(part);
    if (!match) return { key: part, indexes: [] };

    const indexes: number[] = [];
    INDEX_PATTERN.lastIndex = 0;
    let indexMatch = INDEX_PATTERN.exec(match[2]);
    while (indexMatch) {
      indexes.push(Number(indexMatch[1]));
      indexMatch = INDEX_PATTERN.exec(match[2]);
    }

    return { key: match[1], indexes };
  });

  const template = segments
    .map((segment) => segment.key + segment.indexes.map(() => '[]').join(''))
    .join('.');

  const withIndex = segments.find((segment) => segment.indexes.length > 0);

  return {
    raw,
    segments,
    template,
    rowIndex: withIndex ? withIndex.indexes[0] : null,
    leaf: segments.length ? segments[segments.length - 1].key : raw,
  };
}

// ============================================
// Moi lỗi ra khỏi đủ mọi hình dạng error
// ============================================

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as UnknownRecord;
}

/** Khoá của `errors` (dạng CŨ) mang nghĩa "lỗi cấp object", không phải tên ô nhập. */
const LEGACY_OBJECT_LEVEL_KEYS = new Set(['non_field_errors', 'detail']);

/**
 * Những chỗ envelope lỗi có thể nằm.
 *
 * `ApiClientError` giữ body thô ở `data`; lỗi axios chưa qua `parseAxiosError`
 * để ở `response.data`; body đọc bằng `readJsonSafely` thì chính nó là
 * envelope. Duyệt hết để util dùng được cho cả ba đường.
 */
function collectCandidateBodies(error: unknown): UnknownRecord[] {
  const root = asRecord(error);
  if (!root) return [];

  const bodies = [root, ...collectNestedBodies(root)];
  return bodies.filter((body): body is UnknownRecord => body !== null);
}

/**
 * Chỉ những body LỒNG BÊN TRONG, không tính chính object lỗi.
 *
 * Cần tách ra vì `message`: `AxiosError` là một `Error` thật với
 * `message = "Request failed with status code 400"` — câu vô dụng đó nằm ngay
 * ở gốc và sẽ che mất câu BE gửi trong `response.data.message` nếu duyệt theo
 * thứ tự gốc-trước.
 */
function collectNestedBodies(root: UnknownRecord): UnknownRecord[] {
  const original = asRecord(root.originalError);
  const bodies = [
    asRecord(root.data),
    asRecord(asRecord(root.response)?.data),
    original ? asRecord(asRecord(original.response)?.data) : null,
  ];

  return bodies.filter((body): body is UnknownRecord => body !== null);
}

function normalizeDetailEntries(raw: unknown): NormalizedApiErrorDetail[] {
  if (!Array.isArray(raw)) return [];

  return raw.flatMap<NormalizedApiErrorDetail>((entry) => {
    const item = asRecord(entry);
    if (!item) return [];

    const message = typeof item.message === 'string' ? item.message.trim() : '';
    const code = typeof item.code === 'string' && item.code.trim() ? item.code.trim() : null;
    if (!message && !code) return [];

    const rawField =
      typeof item.field === 'string' && item.field.trim() ? item.field.trim() : null;
    // BE mới KHÔNG bao giờ gửi chuỗi `non_field_errors` (đã chốt trả `null`),
    // nhưng `ApiClientError.details` cũ — cũng đi qua hàm này — thì có.
    const field = rawField && LEGACY_OBJECT_LEVEL_KEYS.has(rawField) ? null : rawField;

    return [{ field, code, message }];
  });
}

/**
 * Trải `errors` kiểu cũ thành cùng một hình dạng, `code: null`.
 *
 * Chỉ dùng khi KHÔNG có `error_details` (BE cũ). Giá trị không phải chuỗi bị bỏ
 * qua: `{feedback_id: "..."}`, `{unmatched: [...]}` là dữ liệu, không phải câu
 * để hiện cho người dùng.
 */
function normalizeLegacyErrors(raw: unknown): NormalizedApiErrorDetail[] {
  const errors = asRecord(raw);
  if (!errors) return [];

  return Object.entries(errors).flatMap<NormalizedApiErrorDetail>(([field, value]) => {
    const messages = Array.isArray(value) ? value : [value];
    // Dạng CŨ vẫn dùng chuỗi `non_field_errors` cho lỗi cấp object (dạng mới
    // trả `field: null`). Quy về `null` để cả hai đi chung một nhánh xử lý.
    const normalizedField = LEGACY_OBJECT_LEVEL_KEYS.has(field) ? null : field;

    return messages
      .filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
      .map((message) => ({ field: normalizedField, code: null, message: message.trim() }));
  });
}

/**
 * Danh sách `{field, code, message}` đã chuẩn hoá từ một lỗi BẤT KỲ.
 *
 * Thứ tự nguồn: `errorDetails` (đã parse trong api-client) -> `error_details`
 * (body thô) -> `details` cũ -> `errors` cũ. Dừng ở nguồn đầu tiên có dữ liệu:
 * trộn hai nguồn chỉ tạo ra thông báo lặp cho cùng một field.
 */
export function getApiErrorDetails(error: unknown): NormalizedApiErrorDetail[] {
  for (const body of collectCandidateBodies(error)) {
    const fromNew = normalizeDetailEntries(body.errorDetails ?? body.error_details);
    if (fromNew.length) return fromNew;
  }

  for (const body of collectCandidateBodies(error)) {
    // `details` của `ApiClientError` đã là `{field, message}` — cũng là `errors`
    // đã trải phẳng, chỉ khác là không có mã.
    const fromLegacyDetails = normalizeDetailEntries(body.details);
    if (fromLegacyDetails.length) return fromLegacyDetails;

    const fromLegacyErrors = normalizeLegacyErrors(body.errors);
    if (fromLegacyErrors.length) return fromLegacyErrors;
  }

  return [];
}

/**
 * Mã máy cấp ngoài (`error_code`), hoặc mã tổng hợp cho lỗi mạng/timeout.
 *
 * `ApiClientError.code` chính là `error_code` — đọc cả hai tên vì lỗi có thể
 * chưa đi qua `parseAxiosError`.
 */
export function getApiErrorCode(error: unknown): string | null {
  const root = asRecord(error);
  if (root) {
    if (root.isNetworkError === true) return 'network_error';
    if (root.isTimeout === true) return 'timeout';
  }

  for (const body of collectCandidateBodies(error)) {
    // `code` của envelope BE là HTTP status dạng SỐ (bị loại bởi typeof), còn
    // `ApiClientError.code` chính là `error_code`. Nhưng `AxiosError.code` là
    // mã VẬN CHUYỂN viết HOA (`ERR_NETWORK`, `ECONNABORTED`) — không phải mã
    // nghiệp vụ, và mã của BE thì luôn snake_case thường.
    const code = body.error_code ?? body.code;
    if (typeof code !== 'string' || !code.trim()) continue;

    const normalized = code.trim();
    if (/^[A-Z0-9_]+$/.test(normalized)) continue;

    return normalized;
  }

  return null;
}

/**
 * Câu do server gửi, nếu có.
 *
 * Duyệt body LỒNG trước rồi mới tới gốc: với `AxiosError` chưa qua
 * `parseAxiosError`, `error.message` là "Request failed with status code 400"
 * — đúng cái câu vô nghĩa mà toàn bộ việc này sinh ra để loại bỏ.
 */
function getServerMessage(error: unknown): string | null {
  const root = asRecord(error);
  const bodies = root ? [...collectNestedBodies(root), root] : [];

  for (const body of bodies) {
    if (typeof body.message === 'string' && body.message.trim()) {
      return body.message.replace(/\s+/g, ' ').trim();
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message.replace(/\s+/g, ' ').trim();
  }

  return null;
}

// ============================================
// API dùng ngoài
// ============================================

export interface ApiErrorFormatOptions {
  /**
   * Nhãn hiển thị cho field. Tra theo đường dẫn nguyên văn
   * (`items[0].quantity`) TRƯỚC, rồi tới dạng template (`items[].quantity`) —
   * nhờ vậy khai báo một lần là dùng được cho mọi dòng của mảng.
   */
  fieldLabels?: Record<string, string>;
  /**
   * Đổi TÊN FIELD của BE sang tên ô nhập của form: `{'alcohol_curfew.timezone':
   * 'curfew_timezone'}`.
   *
   * VÌ SAO CẦN: BE đặt tên theo cây serializer nên field của serializer LỒNG đi
   * kèm cả đường dẫn cha (`alcohol_curfew.timezone`), trong khi form ở FE là một
   * object PHẲNG với tên riêng của nó (`curfew_timezone`). Hai bên KHÔNG BAO GIỜ
   * trùng nhau một cách tình cờ, nên nếu dùng thẳng `detail.field` làm khoá thì
   * lỗi của mọi field lồng sẽ không khớp ô nào và ô đó im lặng không đỏ — người
   * dùng thấy toast báo lỗi mà không biết sửa ở đâu.
   *
   * Tra giống `fieldLabels`: đường dẫn NGUYÊN VĂN (`items[0].quantity`) trước,
   * rồi tới dạng template (`items[].quantity`) — khai một lần dùng cho mọi dòng.
   *
   * Bảng ánh xạ là chuyện RIÊNG CỦA TỪNG MÀN (chỉ màn đó biết ô của nó tên gì),
   * nên phía gọi truyền vào; đừng hardcode cặp nào trong util dùng chung này.
   */
  fieldAliases?: Record<string, string>;
  /** Câu dùng khi không moi được gì. Mặc định: `t('apiErrorUnexpected')`. */
  fallback?: string;
  /** Số dòng tối đa gộp vào một toast (mặc định 3). */
  maxMessages?: number;
}

/** Câu đã dịch cho MỘT lỗi: ưu tiên mã, không map được thì dùng câu của server. */
function resolveDetailMessage(detail: NormalizedApiErrorDetail, t: TranslateFn): string | null {
  return translateApiErrorCode(detail.code, t) ?? (detail.message.trim() || null);
}

/**
 * Tra một bảng khai theo ĐƯỜNG DẪN FIELD: nguyên văn trước, template sau.
 *
 * Dùng chung cho `fieldLabels` và `fieldAliases` để hai tuỳ chọn có ĐÚNG MỘT
 * quy tắc tra — khai `items[].quantity` là trúng mọi dòng, còn muốn đặc cách một
 * dòng thì khai `items[0].quantity`.
 */
function lookupByFieldPath(
  path: ParsedFieldPath,
  table: Record<string, string> | undefined
): string | null {
  if (!table) return null;
  return table[path.raw] ?? table[path.template] ?? null;
}

function resolveFieldLabel(
  path: ParsedFieldPath,
  fieldLabels: Record<string, string> | undefined
): string | null {
  return lookupByFieldPath(path, fieldLabels);
}

/**
 * Ghép "nhãn field" + "câu lỗi" thành một dòng đọc được.
 *
 * Không có nhãn thì CHỈ hiện câu lỗi — đường dẫn thô (`supplier_id`) là tên
 * field của BE, hiện ra chỉ khiến người dùng hoang mang.
 */
function formatDetailLine(
  detail: NormalizedApiErrorDetail,
  t: TranslateFn,
  options: ApiErrorFormatOptions
): string | null {
  const message = resolveDetailMessage(detail, t);
  if (!message) return null;
  if (detail.field === null) return message;

  const path = parseFieldPath(detail.field);
  const label = resolveFieldLabel(path, options.fieldLabels);

  if (path.rowIndex === null && !label) return message;

  const row = path.rowIndex === null ? null : path.rowIndex + 1;
  let prefix: string | null;
  if (row !== null && label) {
    prefix = translateOrNull(t, 'apiErrorRowFieldLabel', { row, field: label }) ?? `#${row} ${label}`;
  } else if (row !== null) {
    prefix = translateOrNull(t, 'apiErrorRowLabel', { row }) ?? `#${row}`;
  } else {
    prefix = label;
  }

  if (!prefix) return message;
  return translateOrNull(t, 'apiErrorFieldLine', { field: prefix, message }) ?? `${prefix}: ${message}`;
}

/**
 * Lỗi theo từng ô nhập: `Record<đường dẫn field, câu đã dịch>`.
 *
 * Khoá MẶC ĐỊNH là đường dẫn NGUYÊN VĂN của BE (`items[0].quantity`); cần tách
 * ra thì dùng `parseFieldPath`. Lỗi cấp object (`field: null`) KHÔNG có ở đây —
 * nó thuộc về `getApiErrorMessage`.
 *
 * Tên field của BE chỉ trùng tên ô của form khi form phẳng và đặt tên y hệt
 * serializer. Với field LỒNG (`alcohol_curfew.timezone`) thì không bao giờ trùng
 * — truyền `options.fieldAliases` để đổi khoá về tên ô thật của form, nếu không
 * lỗi đó rơi vào một khoá chẳng ô nào đọc và ô liên quan sẽ không đỏ.
 *
 * Một field nhiều lỗi thì giữ câu ĐẦU TIÊN: DRF xếp lỗi quan trọng nhất trước,
 * và ô nhập chỉ có chỗ cho một dòng. Lưu ý: nhiều đường dẫn BE có thể cùng ánh
 * xạ về MỘT ô — khi đó cũng là câu đầu tiên thắng, đúng quy tắc trên.
 */
export function getApiFieldErrors(
  error: unknown,
  t: TranslateFn,
  options: ApiErrorFormatOptions = {}
): Record<string, string> {
  const result: Record<string, string> = {};

  for (const detail of getApiErrorDetails(error)) {
    if (detail.field === null) continue;

    const path = parseFieldPath(detail.field);
    const key = lookupByFieldPath(path, options.fieldAliases) ?? detail.field;
    if (result[key] !== undefined) continue;

    const message = resolveDetailMessage(detail, t);
    if (message) result[key] = message;
  }

  return result;
}

/**
 * Một câu duy nhất để hiện toast/banner.
 *
 * Thứ tự: lỗi cấp object (`field: null`) -> lỗi theo field -> `error_code` cấp
 * ngoài -> `message` của server -> fallback. Với BE CŨ, hai nhánh đầu rỗng và
 * nhánh `message` cho đúng hành vi hiện tại; BE MỚI bật lên là tự động tốt hơn
 * mà không phải sửa chỗ gọi.
 */
export function getApiErrorMessage(
  error: unknown,
  t: TranslateFn,
  options: ApiErrorFormatOptions = {}
): string {
  const fallback = options.fallback ?? translateOrNull(t, 'apiErrorUnexpected') ?? 'Something went wrong. Please try again.';
  const maxMessages = options.maxMessages ?? 3;

  // Mất mạng / timeout: body không tồn tại, và câu của axios là tiếng Anh cứng.
  const transportCode = getApiErrorCode(error);
  if (transportCode === 'network_error' || transportCode === 'timeout') {
    return translateApiErrorCode(transportCode, t) ?? getServerMessage(error) ?? fallback;
  }

  const details = getApiErrorDetails(error);
  const objectLevel = details.filter((detail) => detail.field === null);
  const fieldLevel = details.filter((detail) => detail.field !== null);

  const serverMessage = getServerMessage(error);
  const usableServerMessage =
    serverMessage && serverMessage.toLowerCase() !== DRF_VALIDATION_MESSAGE ? serverMessage : null;

  /**
   * `errors` kiểu CŨ không phân biệt được "lỗi field" với "dữ liệu kèm lỗi".
   *
   * BE cố ý trả một số response dạng `{errors: {unmatched: ["id1", "id2"]}}` hoặc
   * `{errors: {reason: "duplicate_feedback", feedback_id: "..."}}` — đó là payload
   * để code xử lý, không phải câu cho người đọc. Trải phẳng chúng ra toast sẽ hiện
   * đúng chuỗi ID trần trước mặt người dùng.
   *
   * Không có cách nào phân biệt bằng hình dạng, nhưng có một tín hiệu chắc chắn:
   * `error_details` của BE mới LUÔN kèm `code`. Vậy nên khi lỗi cấp field không có
   * mã nào (tức đến từ nhánh `errors` cũ) thì `message` của server đáng tin hơn —
   * đó là câu BE thật sự viết ra để hiển thị. Lỗi cấp object (`non_field_errors`,
   * `detail`) không dính vấn đề này vì bản thân nó đã là câu hoàn chỉnh.
   */
  const fieldLevelIsUntyped = fieldLevel.length > 0 && fieldLevel.every((detail) => detail.code === null);
  if (!objectLevel.length && fieldLevelIsUntyped && usableServerMessage) {
    return usableServerMessage;
  }

  const lines = (objectLevel.length ? objectLevel : fieldLevel)
    .map((detail) => formatDetailLine(detail, t, options))
    .filter((line): line is string => Boolean(line));

  const unique = Array.from(new Set(lines));
  if (unique.length) return unique.slice(0, maxMessages).join(' ');

  const codeMessage = translateApiErrorCode(transportCode, t);
  if (codeMessage) return codeMessage;

  if (usableServerMessage) return usableServerMessage;

  return fallback;
}
